/**
 * Agent 通知 API（PAGE-AGT-SET-007；后端 AgentSettlementDomainController）
 *
 * GET  /notices?type=&read=&page=&pageSize=
 * POST /notices/{id}/read          （幂等，agentPost 自动注入 Idempotency-Key）
 * POST /notices/read-all           （幂等）
 * GET  /notices/unread-count
 *
 * 红线 RL-001：通知禁关联商户名 / 交易笔数 / GMV / 持卡人（mock 同样遵守）。
 */
import { agentGet, agentPost, USE_MOCK } from './client';

// ---- 后端 DTO（Result.data，camelCase） ----

/** 对齐 AgentNoticeView */
export type AgentNoticeView = {
  noticeId: string;
  title: string;
  content: string;
  /** AUTHORIZED_PRICE / SETTLEMENT_ARRIVED / PLATFORM_POLICY */
  type: string;
  /** INFO / WARNING / IMPORTANT */
  level: string;
  createdAt: string;
  read: boolean;
  /** 价差金额（可空；仅价差科目金额，RL-001） */
  amount?: string | null;
};

/** 对齐 AgentNoticeListResult */
type AgentNoticeListResult = {
  total: number;
  items: AgentNoticeView[];
};

/** 对齐 AgentNoticeUnreadCount */
type AgentNoticeUnreadCount = {
  unreadCount: number;
};

// ---- mock（最小演示数据；无商户名/交易明细，遵守 RL-001） ----

const MOCK_NOTICES: AgentNoticeView[] = [
  {
    noticeId: 'NTF-AGT-001',
    title: '授权价调整 · 虚拟卡 Visa',
    content: '虚拟卡 Visa 产品授权成本价将于下期生效，详情见授权价目表。',
    type: 'AUTHORIZED_PRICE',
    level: 'INFO',
    createdAt: '2026-08-05T09:30:00',
    read: false,
    amount: null,
  },
  {
    noticeId: 'NTF-AGT-002',
    title: '2026-07 结算已到账',
    content: '2026-07 价差结算已完成，到账价差金额 $12,395.50。',
    type: 'SETTLEMENT_ARRIVED',
    level: 'WARNING',
    createdAt: '2026-08-01T00:00:00',
    read: false,
    amount: '12395.50',
  },
  {
    noticeId: 'NTF-AGT-003',
    title: '平台政策更新',
    content: '结算周期变更冷却期调整为 30 天，请查看结算周期配置。',
    type: 'PLATFORM_POLICY',
    level: 'IMPORTANT',
    createdAt: '2026-07-28T16:00:00',
    read: true,
    amount: null,
  },
];

function mockFiltered(params: { type?: string; read?: boolean; page?: number; pageSize?: number }): AgentNoticeListResult {
  let items = [...MOCK_NOTICES];
  if (params.type) {
    items = items.filter((n) => n.type === params.type);
  }
  if (params.read != null) {
    items = items.filter((n) => n.read === params.read);
  }
  const page = params.page ?? 1;
  const pageSize = params.pageSize ?? 20;
  const start = (page - 1) * pageSize;
  return { total: items.length, items: items.slice(start, start + pageSize) };
}

// ---- API ----

/** 通知分页列表（服务端筛选 type/read）。 */
export async function listNotices(params?: {
  type?: string;
  read?: boolean;
  page?: number;
  pageSize?: number;
}) {
  const qs = new URLSearchParams({
    page: String(params?.page ?? 1),
    pageSize: String(params?.pageSize ?? 20),
  });
  if (params?.type) qs.set('type', params.type);
  if (params?.read != null) qs.set('read', String(params.read));
  return agentGet<AgentNoticeListResult>(`/notices?${qs.toString()}`, () =>
    mockFiltered(params ?? {}),
  );
}

/** 单条标记已读（幂等）。 */
export async function markNoticeRead(id: string) {
  return agentPost<null>(`/notices/${id}/read`, undefined, () => null);
}

/** 批量全部已读（幂等）。 */
export async function markAllNoticesRead() {
  return agentPost<null>('/notices/read-all', undefined, () => null);
}

/** 未读数（页面头部红点 / 菜单 badge）。 */
export async function fetchNoticeUnreadCount() {
  return agentGet<AgentNoticeUnreadCount>(
    '/notices/unread-count',
    () => ({ unreadCount: MOCK_NOTICES.filter((n) => !n.read).length }),
  );
}

// ---- 展示辅助 ----

// 时间展示统一（P3）：api 层返回原始 ISO，展示层用 @/lib/format formatDateTime。

export const NOTICE_TYPE_LABEL: Record<string, string> = {
  AUTHORIZED_PRICE: '授权价调整',
  SETTLEMENT_ARRIVED: '结算到账',
  PLATFORM_POLICY: '平台政策',
};

export const NOTICE_LEVEL_LABEL: Record<string, string> = {
  INFO: '提示',
  WARNING: '关注',
  IMPORTANT: '高优',
};

export const NOTICE_LEVEL_STYLE: Record<string, string> = {
  INFO: 'bg-blue-50 text-blue-700',
  WARNING: 'bg-amber-50 text-amber-700',
  IMPORTANT: 'bg-rose-50 text-rose-700',
};

export { USE_MOCK, MOCK_NOTICES };
